import baseAPI from "@/api/base";
import { AxiosResponse } from "axios";

export interface EmailSignature {
  id: string;
  userId: string;
  name: string;
  content: string;
  isDefault: boolean;
  organizationId: string;
  isActive: boolean;
  isDeleted: boolean;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSignatureRequest {
  name: string;
  content: string;
  isDefault?: boolean;
}

export interface UpdateSignatureRequest {
  name?: string;
  content?: string;
  isDefault?: boolean;
}

class EmailSignatureService {
  private static BASE_URL = "/email-signatures";

  /**
   * Get all email signatures for the authenticated user
   */
  static getSignatures = async (): Promise<
    AxiosResponse<{ status: boolean; data: EmailSignature[] }>
  > => {
    const response: AxiosResponse = await baseAPI.get(this.BASE_URL);
    return response;
  };

  /**
   * Get a single email signature by ID
   */
  static getSignature = async (
    id: string
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSignature }>> => {
    const response: AxiosResponse = await baseAPI.get(`${this.BASE_URL}/${id}`);
    return response;
  };

  /**
   * Get the default email signature
   */
  static getDefaultSignature = async (): Promise<
    AxiosResponse<{ status: boolean; data: EmailSignature | null }>
  > => {
    const response: AxiosResponse = await baseAPI.get(`${this.BASE_URL}/default`);
    return response;
  };

  /**
   * Create a new email signature
   */
  static createSignature = async (
    data: CreateSignatureRequest
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSignature }>> => {
    const response: AxiosResponse = await baseAPI.post(this.BASE_URL, data);
    return response;
  };

  /**
   * Create a new email signature with files (multipart/form-data)
   */
  static createSignatureWithFiles = async (
    formData: FormData
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSignature }>> => {
    const response: AxiosResponse = await baseAPI.post(this.BASE_URL, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  };

  /**
   * Update an existing email signature
   */
  static updateSignature = async (
    id: string,
    data: UpdateSignatureRequest
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSignature }>> => {
    const response: AxiosResponse = await baseAPI.put(`${this.BASE_URL}/${id}`, data);
    return response;
  };

  /**
   * Update an existing email signature with files (multipart/form-data)
   */
  static updateSignatureWithFiles = async (
    id: string,
    formData: FormData
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSignature }>> => {
    const response: AxiosResponse = await baseAPI.put(`${this.BASE_URL}/${id}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response;
  };

  /**
   * Delete an email signature
   */
  static deleteSignature = async (
    id: string
  ): Promise<AxiosResponse<{ status: boolean; data: { message: string } }>> => {
    const response: AxiosResponse = await baseAPI.delete(`${this.BASE_URL}/${id}`);
    return response;
  };

  /**
   * Set a signature as default
   */
  static setDefaultSignature = async (
    id: string
  ): Promise<AxiosResponse<{ status: boolean; data: EmailSignature }>> => {
    const response: AxiosResponse = await baseAPI.patch(`${this.BASE_URL}/${id}/set-default`);
    return response;
  };
}

export default EmailSignatureService;
